import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Users, MousePointerClick, DollarSign, FileText, TrendingUp, UserPlus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChatterDashboard } from '../components/chatting/ChatterDashboard';
import { ChattingOverview } from '../components/chatting/ChattingOverview';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.va_role === 'Chatting VA') return <ChatterDashboard />;
  if (user?.va_role === 'Manager Chatting VA') return <ChattingOverview />;
  return <MainDashboard />;
}

function MainDashboard() {
  const { user } = useAuth();
  const isAccountCreationVA = user?.va_role === 'Account Creation VA';
  const [stats, setStats] = useState(null);
  const [acStats, setAcStats] = useState(null); // account-creation-specific stats
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('today');
  const timeframeRef = useRef(timeframe);

  // Keep ref in sync with state
  useEffect(() => {
    timeframeRef.current = timeframe;
  }, [timeframe]);

  useEffect(() => {
    // Auto-sync every 5 minutes silently in the background.
    // The manual "Sync Now" button lives in Admin Settings, not here.
    const syncInterval = setInterval(() => {
      handleSilentSync();
    }, 300000);

    return () => clearInterval(syncInterval);
  }, []);

  useEffect(() => {
    fetchStats();
  }, [timeframe]);

  const fetchStats = async () => {
    try {
      if (isAccountCreationVA) {
        const r = await axios.get(`${API_URL}/account-creation/dashboard-stats`, { params: { timeframe } });
        setAcStats(r.data);
      } else {
        const response = await axios.get(`${API_URL}/dashboard/stats`, { params: { timeframe } });
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to fetch dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const handleSilentSync = async () => {
    try {
      if (isAccountCreationVA) {
        const r = await axios.get(`${API_URL}/account-creation/dashboard-stats`, { params: { timeframe: timeframeRef.current } });
        setAcStats(r.data);
        return;
      }
      await axios.get(`${API_URL}/clicks/sync`);
      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        params: { timeframe: timeframeRef.current }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Auto-sync failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Account Creation VA dashboard: 2 metrics + comparison line.
  // ─────────────────────────────────────────────────────────────
  if (isAccountCreationVA) {
    const prevLabel = ({
      'today': 'yesterday',
      'yesterday': 'day before',
      'this-week': 'last week',
      'last-week': 'week before',
      'this-month': 'last month',
      'last-month': 'month before',
      'this-year': 'last year',
      'last-year': 'year before',
    })[timeframe] || 'previous';
    const change = acStats?.change_percent ?? 0;
    return (
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="dashboard-title">
              Dashboard
            </h1>
            <p className="text-base text-muted-foreground mt-2">Welcome back, {user?.name}</p>
          </div>
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-48 bg-zinc-900/50 border-zinc-800 text-white" data-testid="timeframe-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="this-week">This Week</SelectItem>
              <SelectItem value="last-week">Last Week</SelectItem>
              <SelectItem value="this-month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="this-year">This Year</SelectItem>
              <SelectItem value="last-year">Last Year</SelectItem>
              <SelectItem value="all-time">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="metric-card bg-card p-6 rounded-md border border-blue-500/20" data-testid="metric-accounts-created">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Accounts Created</span>
              <div className="bg-blue-500/10 text-blue-500 p-2 rounded-md">
                <UserPlus className="w-5 h-5" strokeWidth={1.5} />
              </div>
            </div>
            <div className="text-5xl md:text-6xl font-bold font-heading tracking-tighter tabular-nums text-white">
              {(acStats?.accounts_created ?? 0).toLocaleString()}
            </div>
          </div>

          <div className="metric-card bg-card p-6 rounded-md border border-green-500/20" data-testid="metric-total-earnings">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Total Earnings</span>
              <div className="bg-green-500/10 text-green-500 p-2 rounded-md">
                <DollarSign className="w-5 h-5" strokeWidth={1.5} />
              </div>
            </div>
            <div className="text-5xl md:text-6xl font-bold font-heading tracking-tighter tabular-nums text-white">
              ${(acStats?.earnings ?? 0).toFixed(2)}
            </div>
            <div className="mt-2 text-sm text-green-500 font-medium">
              ${(acStats?.price_per_valid ?? 0).toFixed(4)} per valid account
            </div>
          </div>
        </div>

        <div className="bg-card p-6 rounded-md border border-border">
          <h2 className="text-2xl font-semibold font-heading text-white mb-4">Performance Details</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-md" data-testid="comparison-row">
              <span className="text-zinc-300">Comparison — Accounts Created</span>
              <div className="text-right">
                <span className="text-white font-semibold">{(acStats?.previous_accounts_created ?? 0).toLocaleString()}</span>
                <span className="text-zinc-500 text-sm ml-1">({prevLabel})</span>
                <span className={`ml-2 text-sm font-medium ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {change >= 0 ? '+' : ''}{change.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Head Manager VA sees the same agency-wide dashboard as admin.
  const isAgencyViewer = user?.role === 'admin' || user?.va_role === 'Head Manager VA';

  const metrics = isAgencyViewer ? [
    {
      title: 'Total VAs',
      value: stats?.total_vas || 0,
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/20',
    },
    {
      title: 'Total Clicks',
      value: stats?.total_clicks?.toLocaleString() || 0,
      icon: MousePointerClick,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
    },
    {
      title: 'T1 Clicks',
      value: stats?.total_t1_clicks?.toLocaleString() || 0,
      icon: TrendingUp,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
    },
    {
      title: 'Total Costs',
      value: `$${stats?.total_earnings?.toFixed(2) || '0.00'}`,
      subValue: stats?.total_bonus > 0 ? `incl. bonus: +$${stats.total_bonus.toFixed(2)}` : null,
      subValueColor: 'text-red-500',
      icon: DollarSign,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20',
    },
  ] : [
    {
      title: 'Total Clicks',
      value: stats?.total_clicks?.toLocaleString() || 0,
      icon: MousePointerClick,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20',
    },
    {
      title: 'T1 Clicks',
      value: stats?.total_t1_clicks?.toLocaleString() || 0,
      icon: TrendingUp,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
    },
    {
      title: 'Wasted Clicks',
      value: (stats?.total_clicks - stats?.total_t1_clicks)?.toLocaleString() || 0,
      icon: MousePointerClick,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/20',
    },
    {
      title: 'Total Earnings',
      value: `$${stats?.total_earnings?.toFixed(2) || '0.00'}`,
      subValue: stats?.total_bonus > 0 ? `incl. bonus: +$${stats.total_bonus.toFixed(2)}` : null,
      subValueColor: 'text-green-500',
      icon: DollarSign,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/20',
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-white tracking-tight" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-base text-muted-foreground mt-2">Welcome back, {user?.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-48 bg-zinc-900/50 border-zinc-800 text-white" data-testid="timeframe-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="this-week">This Week</SelectItem>
              <SelectItem value="last-week">Last Week</SelectItem>
              <SelectItem value="this-month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="this-year">This Year</SelectItem>
              <SelectItem value="last-year">Last Year</SelectItem>
              <SelectItem value="all-time">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <div
            key={index}
            className={`metric-card bg-card p-6 rounded-md border ${metric.borderColor}`}
            data-testid={`metric-${metric.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">
                {metric.title}
              </span>
              <div className={`${metric.bgColor} ${metric.color} p-2 rounded-md`}>
                <metric.icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
            </div>
            <div className="text-5xl md:text-6xl font-bold font-heading tracking-tighter tabular-nums text-white">
              {metric.value}
            </div>
            {metric.subValue && (
              <div className={`mt-2 text-sm ${metric.subValueColor || 'text-green-500'} font-medium`}>
                {metric.subValue}
              </div>
            )}
          </div>
        ))}
      </div>

      {stats?.pending_invoices > 0 && isAgencyViewer && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-md p-6">
          <div className="flex items-start gap-4">
            <FileText className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">Pending Invoices</h3>
              <p className="text-sm text-zinc-400">
                You have {stats.pending_invoices} pending invoice{stats.pending_invoices !== 1 ? 's' : ''} waiting for payment.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card p-6 rounded-md border border-border">
        <h2 className="text-2xl font-semibold font-heading text-white mb-4">Performance Details</h2>
        <div className="space-y-4">
          <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-md">
            <span className="text-zinc-300">Wasted Clicks</span>
            <span className="text-white font-semibold">
              {stats?.total_clicks > 0
                ? `${((stats.total_clicks - stats.total_t1_clicks) / stats.total_clicks * 100).toFixed(1)}%`
                : '0%'}
            </span>
          </div>
          <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-md">
            <span className="text-zinc-300">T1 Clicks</span>
            <span className="text-white font-semibold">
              {stats?.total_clicks > 0
                ? `${(stats.total_t1_clicks / stats.total_clicks * 100).toFixed(1)}%`
                : '0%'}
            </span>
          </div>
          <div className="flex justify-between items-center p-4 bg-zinc-900/50 rounded-md">
            <span className="text-zinc-300">T1 Click Comparison</span>
            <div className="text-right">
              {stats?.last_period_t1_clicks !== null && stats?.last_period_t1_clicks !== undefined ? (
                <>
                  <span className="text-white font-semibold">{stats.last_period_t1_clicks.toLocaleString()}</span>
                  <span className="text-zinc-500 text-sm ml-1">
                    ({timeframe === 'this-week' ? 'last week' : 
                      timeframe === 'this-month' ? 'last month' : 
                      timeframe === 'this-year' ? 'last year' : 
                      timeframe === 'today' ? 'yesterday' : 'prev'})
                  </span>
                  {stats?.t1_clicks_change !== null && stats?.t1_clicks_change !== undefined && (
                    <span className={`ml-2 text-sm font-medium ${stats.t1_clicks_change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {stats.t1_clicks_change >= 0 ? '+' : ''}{stats.t1_clicks_change.toFixed(1)}%
                    </span>
                  )}
                </>
              ) : (
                <span className="text-zinc-500 text-sm">N/A</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}